import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';

import { ExternalLink, LinkedLogo } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import FooterNewsletter from './FooterNewsletter';

import appleIcon from '../../../../assets/footer/icon-apple.svg';
import appStoreWordmark from '../../../../assets/footer/wordmark-app-store.svg';
import googlePlayIcon from '../../../../assets/footer/icon-google-play.svg';
import googlePlayWordmark from '../../../../assets/footer/wordmark-google-play.svg';
import css from './SectionFooter.module.css';

const GRID_CONFIG = [
  { gridCss: css.gridCol1 },
  { gridCss: css.gridCol2 },
  { gridCss: css.gridCol3 },
  { gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;
const APP_STORE_HREF = 'https://apps.apple.com';
const PLAY_STORE_HREF = 'https://play.google.com/store';

const getIndex = numberOfColumns => numberOfColumns - 1;

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Footer section: logo + store badges, Console link columns, copyright, social icons.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {string} props.sectionId
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks
 * @param {Object} props.options
 * @returns {JSX.Element}
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns = 1,
    socialMediaLinks = [],
    appearance,
    copyright,
    blocks = [],
    options,
    linkLogoToExternalSite,
  } = props;

  const intl = useIntl();
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';
  const appStoreLabel = intl.formatMessage({ id: 'SectionFooter.appStoreAria' });
  const playStoreLabel = intl.formatMessage({ id: 'SectionFooter.playStoreAria' });

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <FooterNewsletter />
        <div className={css.main}>
          <div className={css.brand}>
            <LinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              linkToExternalSite={linkLogoToExternalSite}
              layout={logoLayout}
            />
            <div className={css.appStores}>
              <ExternalLink
                className={css.storeBadge}
                href={APP_STORE_HREF}
                title={appStoreLabel}
              >
                <img className={css.storeIcon} src={appleIcon} alt="" width="14" height="14" />
                <span className={css.storeCopy}>
                  <span className={css.storeEyebrow}>
                    <FormattedMessage id="SectionFooter.appStoreEyebrow" />
                  </span>
                  <img
                    className={css.storeWordmark}
                    src={appStoreWordmark}
                    alt=""
                    width="42"
                    height="9"
                  />
                </span>
              </ExternalLink>
              <ExternalLink
                className={css.storeBadge}
                href={PLAY_STORE_HREF}
                title={playStoreLabel}
              >
                <img className={css.storeIcon} src={googlePlayIcon} alt="" width="14" height="14" />
                <span className={css.storeCopy}>
                  <span className={css.storeEyebrow}>
                    <FormattedMessage id="SectionFooter.playStoreEyebrow" />
                  </span>
                  <img
                    className={css.storeWordmarkPlay}
                    src={googlePlayWordmark}
                    alt=""
                    width="46"
                    height="9"
                  />
                </span>
              </ExternalLink>
            </div>
          </div>

          <div className={classNames(css.grid, css.gridWidthContainer, getGridCss(numberOfColumns))}>
            <BlockBuilder blocks={blocks} sectionId={sectionId} options={options} />
          </div>
        </div>
        <div className={css.bottom}>
          <Field data={copyright} className={css.copyright} />
          {showSocialMediaLinks ? (
            <div className={css.icons}>
              <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
            </div>
          ) : null}
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
